/**
 * Bomb HUD — plant/defuse progress bars, bomb planted indicator, bomb timer visual,
 * bomb site markers, and bomb carrier indicator.
 */

import { isMobile } from '../core/device';

const ACCENT = '#e94560';
const BG_DARK = '#1a1a2e';
const T_COLOR = '#e8b849';

export interface BombState {
  planted: boolean;
  site: 'A' | 'B' | null;
  timeRemaining: number; // seconds remaining on bomb timer
  totalTime: number; // total bomb timer (e.g. 40s)
  carrierPlayerId: string | null;
  isLocalCarrier: boolean;
  hasDefuseKit?: boolean;
}

// ---------------------------------------------------------------------------
// Plant / Defuse progress bar (centered on screen)
// ---------------------------------------------------------------------------

let progressContainer: HTMLElement | null = null;
let progressBar: HTMLElement | null = null;
let progressLabel: HTMLElement | null = null;
let progressSecondaryLabel: HTMLElement | null = null;

function ensureProgressBar(): void {
  if (progressContainer) return;

  // BUG-HUD-9: on mobile the plant/defuse progress bar at bottom:30% overlaps
  // the FIRE/ADS button cluster. Raise to bottom:40% and clamp width so it
  // fits on small phones without bumping the right-edge buttons.
  const mobile = isMobile();
  progressContainer = document.createElement('div');
  Object.assign(progressContainer.style, {
    position: 'fixed',
    bottom: mobile ? '40%' : '30%',
    left: '50%',
    transform: 'translateX(-50%)',
    width: mobile ? 'clamp(180px, 60vw, 320px)' : '300px',
    textAlign: 'center',
    zIndex: '45',
    pointerEvents: 'none',
    fontFamily: '"Courier New", monospace',
    display: 'none',
  });

  progressLabel = document.createElement('div');
  Object.assign(progressLabel.style, {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#ffffff',
    letterSpacing: '2px',
    marginBottom: '6px',
    textTransform: 'uppercase',
    textShadow: '0 1px 4px rgba(0,0,0,0.8)',
  });
  progressContainer.appendChild(progressLabel);

  progressSecondaryLabel = document.createElement('div');
  Object.assign(progressSecondaryLabel.style, {
    display: 'none',
    fontSize: '11px',
    color: 'rgba(255,255,255,0.78)',
    letterSpacing: '1px',
    marginBottom: '6px',
    textTransform: 'uppercase',
    textShadow: '0 1px 4px rgba(0,0,0,0.8)',
  });
  progressContainer.appendChild(progressSecondaryLabel);

  const track = document.createElement('div');
  Object.assign(track.style, {
    width: '100%',
    height: '6px',
    background: 'rgba(255,255,255,0.15)',
    borderRadius: '3px',
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.2)',
  });

  progressBar = document.createElement('div');
  Object.assign(progressBar.style, {
    width: '0%',
    height: '100%',
    borderRadius: '3px',
    transition: 'width 0.05s linear',
  });
  track.appendChild(progressBar);
  progressContainer.appendChild(track);

  document.body.appendChild(progressContainer);
}

export function showPlantProgress(progress: number): void {
  ensureProgressBar();
  progressContainer!.style.display = 'block';
  progressLabel!.textContent = 'PLANTING BOMB';
  progressLabel!.style.color = T_COLOR;
  if (progressSecondaryLabel) {
    progressSecondaryLabel.style.display = 'none';
    progressSecondaryLabel.textContent = '';
  }
  progressBar!.style.background = T_COLOR;
  progressBar!.style.width = `${Math.min(100, Math.max(0, progress * 100))}%`;
}

export function showDefuseProgress(progress: number, hasKit: boolean): void {
  ensureProgressBar();
  progressContainer!.style.display = 'block';
  const color = '#5b9bd5';
  progressLabel!.textContent = hasKit ? 'DEFUSING (KIT)' : 'DEFUSING';
  progressLabel!.style.color = color;
  if (progressSecondaryLabel) {
    progressSecondaryLabel.textContent = '(hold E)';
    progressSecondaryLabel.style.display = 'block';
  }
  progressBar!.style.background = color;
  progressBar!.style.width = `${Math.min(100, Math.max(0, progress * 100))}%`;
}

export function hideProgress(): void {
  if (progressContainer) {
    progressContainer.style.display = 'none';
  }
  if (progressSecondaryLabel) {
    progressSecondaryLabel.style.display = 'none';
    progressSecondaryLabel.textContent = '';
  }
}

// ---------------------------------------------------------------------------
// Bomb planted indicator + timer
// ---------------------------------------------------------------------------

let bombContainer: HTMLElement | null = null;
let bombText: HTMLElement | null = null;
let bombTimerEl: HTMLElement | null = null;
let bombSiteEl: HTMLElement | null = null;
let defuseKitEl: HTMLElement | null = null;
let bombTimerBar: HTMLElement | null = null;
let carrierEl: HTMLElement | null = null;
let flashRafId: number | null = null;
let flashPeriodMs = 700;
let flashStartMs = 0;

function requestFlashFrame(): void {
  if (flashRafId !== null) return;
  if (typeof requestAnimationFrame !== 'function') return;
  flashRafId = requestAnimationFrame(updateFlashPulse);
}

function updateFlashPulse(): void {
  flashRafId = null;
  if (!bombText) return;

  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    bombText.style.opacity = '1';
    requestFlashFrame();
    return;
  }

  const elapsed = Math.max(0, Date.now() - flashStartMs);
  const cycle = flashPeriodMs > 0 ? (elapsed % flashPeriodMs) / flashPeriodMs : 0;
  const intensity = 0.5 - Math.cos(cycle * Math.PI * 2) * 0.5;
  bombText.style.opacity = (1 - intensity * 0.7).toFixed(3);
  requestFlashFrame();
}

function startFlashPulse(periodMs: number): void {
  flashPeriodMs = periodMs;
  if (flashStartMs === 0) flashStartMs = Date.now();
  requestFlashFrame();
}

function stopFlashPulse(): void {
  if (flashRafId !== null && typeof cancelAnimationFrame === 'function') {
    cancelAnimationFrame(flashRafId);
  }
  flashRafId = null;
  flashStartMs = 0;
  if (bombText) bombText.style.opacity = '1';
}

function ensureBombHud(): void {
  if (bombContainer) return;

  // BUG-HUD-25: on mobile top:12% sits behind the round-score widget. Anchor
  // the banner under the score row using the same safe-area offset the score
  // widget uses (~80px from top + iOS notch inset).
  const mobile = isMobile();
  bombContainer = document.createElement('div');
  Object.assign(bombContainer.style, {
    position: 'fixed',
    top: mobile ? 'calc(80px + env(safe-area-inset-top, 0px))' : '12%',
    left: '50%',
    transform: 'translateX(-50%)',
    textAlign: 'center',
    zIndex: '42',
    pointerEvents: 'none',
    fontFamily: '"Courier New", monospace',
    display: 'none',
  });

  bombText = document.createElement('div');
  Object.assign(bombText.style, {
    fontSize: '26px',
    fontWeight: 'bold',
    color: ACCENT,
    letterSpacing: '4px',
    textTransform: 'uppercase',
    textShadow: `0 0 15px ${ACCENT}, 0 0 30px rgba(233,69,96,0.4)`,
  });
  bombText.textContent = 'BOMB PLANTED';
  bombContainer.appendChild(bombText);

  // Bomb site indicator
  bombTimerEl = document.createElement('div');
  Object.assign(bombTimerEl.style, {
    fontSize: '14px',
    color: '#ffffff',
    marginTop: '6px',
    letterSpacing: '2px',
  });
  bombSiteEl = document.createElement('span');
  bombTimerEl.appendChild(bombSiteEl);

  defuseKitEl = document.createElement('span');
  Object.assign(defuseKitEl.style, {
    display: 'none',
    marginLeft: '8px',
    fontSize: '13px',
    verticalAlign: 'middle',
  });
  defuseKitEl.title = 'Defuse kit';
  defuseKitEl.textContent = '\u{1F527}';
  bombTimerEl.appendChild(defuseKitEl);
  bombContainer.appendChild(bombTimerEl);

  // Timer bar
  const timerTrack = document.createElement('div');
  Object.assign(timerTrack.style, {
    width: '200px',
    height: '4px',
    background: 'rgba(255,255,255,0.15)',
    borderRadius: '2px',
    overflow: 'hidden',
    margin: '8px auto 0',
  });

  bombTimerBar = document.createElement('div');
  Object.assign(bombTimerBar.style, {
    width: '100%',
    height: '100%',
    background: ACCENT,
    borderRadius: '2px',
    transition: 'width 0.25s linear',
  });
  timerTrack.appendChild(bombTimerBar);
  bombContainer.appendChild(timerTrack);

  document.body.appendChild(bombContainer);

  // Bomb carrier indicator (bottom left for desktop, top-right-of-minimap for
  // mobile so it clears the 50%-tall joystick zone — BUG-HUD-3).
  carrierEl = document.createElement('div');
  Object.assign(carrierEl.style, {
    position: 'fixed',
    ...(mobile
      ? {
          top: 'calc(80px + env(safe-area-inset-top, 0px))',
          right: '220px',
        }
      : {
          bottom: '80px',
          left: '20px',
        }),
    fontSize: '13px',
    fontWeight: 'bold',
    color: T_COLOR,
    letterSpacing: '1px',
    fontFamily: '"Courier New", monospace',
    textShadow: '0 1px 4px rgba(0,0,0,0.8)',
    zIndex: '30',
    pointerEvents: 'none',
    display: 'none',
    padding: '4px 10px',
    border: `1px solid ${T_COLOR}`,
    borderRadius: '3px',
    background: 'rgba(0,0,0,0.6)',
  });
  document.body.appendChild(carrierEl);
}

export function updateBombState(state: BombState): void {
  ensureBombHud();

  if (state.planted) {
    bombContainer!.style.display = 'block';
    carrierEl!.style.display = 'none';

    // Update site
    if (state.site) {
      bombSiteEl!.textContent = `SITE ${state.site}`;
    } else {
      bombSiteEl!.textContent = '';
    }
    defuseKitEl!.style.display = state.hasDefuseKit ? 'inline' : 'none';

    // Timer bar
    const pct =
      state.totalTime > 0 ? Math.max(0, state.timeRemaining / state.totalTime) * 100 : 0;
    bombTimerBar!.style.width = `${pct}%`;

    // Color shifts as timer runs down
    const urgency = state.totalTime > 0 ? state.timeRemaining / state.totalTime : 1;
    if (urgency < 0.25) {
      bombTimerBar!.style.background = '#ff2222';
    } else if (urgency < 0.5) {
      bombTimerBar!.style.background = '#ff6633';
    } else {
      bombTimerBar!.style.background = ACCENT;
    }

    const flashMs = urgency < 0.25 ? 200 : urgency < 0.5 ? 400 : 700;
    startFlashPulse(flashMs);
  } else {
    // Not planted
    bombContainer!.style.display = 'none';
    if (defuseKitEl) defuseKitEl.style.display = 'none';
    stopFlashPulse();

    // Carrier indicator
    if (state.isLocalCarrier) {
      carrierEl!.style.display = 'block';
      carrierEl!.textContent = '';
      const bombIcon = document.createElement('span');
      bombIcon.style.fontSize = '16px';
      bombIcon.textContent = '\u{1F4A3}';
      carrierEl!.appendChild(bombIcon);
      carrierEl!.appendChild(document.createTextNode(' YOU HAVE THE BOMB'));
    } else if (state.carrierPlayerId) {
      carrierEl!.style.display = 'block';
      carrierEl!.textContent = 'TEAMMATE HAS BOMB';
    } else {
      carrierEl!.style.display = 'none';
    }
  }
}

export function hideBombHud(): void {
  if (bombContainer) bombContainer.style.display = 'none';
  if (carrierEl) carrierEl.style.display = 'none';
  if (progressContainer) progressContainer.style.display = 'none';
  stopFlashPulse();
}

export function disposeBombHud(): void {
  hideBombHud();
  if (bombContainer) {
    bombContainer.remove();
    bombContainer = null;
    bombTimerEl = null;
    bombSiteEl = null;
    defuseKitEl = null;
    bombTimerBar = null;
    bombText = null;
  }
  if (carrierEl) {
    carrierEl.remove();
    carrierEl = null;
  }
  if (progressContainer) {
    progressContainer.remove();
    progressContainer = null;
    progressSecondaryLabel = null;
  }
}
